import axios from 'axios';
import { GraphQLClient } from 'graphql-request';
import fetch from 'cross-fetch';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

const REDIRECT_COUNT_KEY = 'challenge_redirect_count';
const REDIRECT_COUNT_MAX = 3;
function getChallengeRedirectCount() {
  var _localStorage$getItem;
  const value = parseInt((_localStorage$getItem = localStorage.getItem(REDIRECT_COUNT_KEY)) !== null && _localStorage$getItem !== void 0 ? _localStorage$getItem : '0');
  return Number.isFinite(value) ? value : 0;
}
function incrementChallengeRedirectCount() {
  const value = getChallengeRedirectCount() + 1;
  localStorage.setItem(REDIRECT_COUNT_KEY, value.toString());
  return value;
}
function handleChallenge() {
  if (typeof window !== 'undefined') {
    const redirectCount = getChallengeRedirectCount();
    if (redirectCount < REDIRECT_COUNT_MAX) {
      incrementChallengeRedirectCount();
      const params = new URLSearchParams();
      params.set('redirect', window.location.href);
      params.set('counter', redirectCount.toString());
      window.location.replace(`/challenge?${params.toString()}`);
    } else {
      console.warn('Maximum challenge redirect count reached.');
    }
  }
}

const _excluded = ["payload"];
function _async$1(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }
    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
let defaultBaseUrl = undefined;
let defaultBaseAdapter = undefined;
function setBaseUrl(baseUrl) {
  defaultBaseUrl = baseUrl;
}
function setBaseAdapter(adapter) {
  defaultBaseAdapter = adapter;
}
const queryStringFromObject = function () {
  let payload = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return Object.entries(payload).filter(_ref => {
    let [, value] = _ref;
    return value !== undefined && value !== null;
  }).map(_ref2 => {
    let [key, value] = _ref2;
    return `${key}=${encodeURIComponent(value)}`;
  }).join('&');
};
const goFetch = (method, config) => {
  const api = axios.create({
    baseURL: config.baseURL || defaultBaseUrl || "https://simplywall.st/",
    timeout: config.timeout ? config.timeout : 0,
    headers: Object.assign({
      Accept: 'application/vnd.simplywallst.v2',
      'X-Requested-With': `sws-services/${"mono-auto-442c9cf4"}`
    }, config.headers),
    adapter: config.adapter || defaultBaseAdapter
  });
  // Attempt to detect Cloudflare challenges and redirect users to `/challenge`
  // if they are detected. This allows us to setup more stringent bot challenges
  // on our APIs while allowing real users to be pushed through a verification
  // flow.
  api.interceptors.response.use(response => response, _async$1(function (error) {
    const {
      response
    } = error;
    if ((response === null || response === void 0 ? void 0 : response.headers['cf-mitigated']) === 'challenge') {
      handleChallenge();
    }
    throw error;
  }));
  return api.request({
    method,
    url: config.url,
    data: config.payload
  });
};
function get(config) {
  const {
      payload
    } = config,
    rest = _objectWithoutPropertiesLoose(config, _excluded);
  const options = Object.assign({}, requestConfigDefaults(), rest, payload ? {
    url: `${rest.url}?${queryStringFromObject(payload)}`
  } : {});
  return goFetch('GET', options);
}
function head(config) {
  const options = Object.assign({}, requestConfigDefaults(), config);
  return goFetch('HEAD', options);
}
const formUrlEncodedConfig = config => {
  return Object.assign({}, config, {
    payload: queryStringFromObject(config.payload),
    headers: Object.assign({}, config.headers, {
      'Content-Type': 'application/x-www-form-urlencoded'
    })
  });
};
const applicationJsonEncodedConfig = config => {
  return Object.assign({}, config, {
    headers: Object.assign({}, config.headers, {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    })
  });
};
const applicationFormData = config => {
  return Object.assign({}, config, {
    payload: config.payload,
    headers: Object.assign({}, config.headers, {
      'Content-Type': 'multipart/form-data'
    })
  });
};
const requestConfigDefaults = () => ({
  payload: {},
  headers: {}
});
function post(config) {
  return goFetch('POST', formUrlEncodedConfig(config));
}
function postJson(config) {
  return goFetch('POST', applicationJsonEncodedConfig(config));
}
function postFormData(config) {
  return goFetch('POST', applicationFormData(config));
}
function del(config) {
  return goFetch('DELETE', formUrlEncodedConfig(config));
}
function put(config) {
  return goFetch('PUT', applicationJsonEncodedConfig(config));
}
function patch(config) {
  return goFetch('PATCH', applicationJsonEncodedConfig(config));
}

function removeLeadingSlash(url) {
  return url.charAt(0) === '/' ? removeLeadingSlash(url.substr(1)) : url;
}
function removeTrailingSlash(url) {
  return url.replace(/\/$/, '');
}

function getServices() {
  return {
    SWS_API_SERVICE: "https://simplywall.st/",
    SWS_BILLING_SERVICE: "https://billing.simplywall.st/",
    SWS_STATEMENTS_API_SERVICE: "https://statements.simplywall.st/",
    SWS_STREAM_API_SERVICE: "https://stream.simplywall.st/",
    SWS_WARREN_SERVICE: "https://swr.simplywall.st/",
    SWS_CLOUDFLARE_WORKER: "https://workers.simplywall.st/",
    SWS_GQL_SERVICE: "https://simplywall.st/graphql",
    SWS_PRO_API_GQL_SERVICE: "https://simplywall.st/backend/pro-api/graphql",
    SWS_MONO_SERVICE: "https://simplywall.st/"
  };
}

function authToken(payload) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return post({
    url: 'backend/auth/token',
    payload,
    headers
  });
}

function createGrid(payload) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return postJson({
    url: 'api/grid/view',
    headers,
    // requiresAuthentication: true,
    payload
  });
}

function deleteGridView(id) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return del({
    url: `api/grid/view/${id}`,
    headers
    // requiresAuthentication: true,
  });
}

function getGridView(id) {
  return get({
    url: `api/grid/view/${id}`
  });
}

function checkGridView (_ref) {
  let {
    id,
    name,
    payload
  } = _ref;
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let axiosConfig = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const config = Object.assign({
    url: `api/grid/view/${id}?slug=${removeLeadingSlash(name)}`,
    payload,
    headers
  }, axiosConfig);
  return head(config);
}

function getUacCheck() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return get({
    url: 'api/uac/check',
    payload: {
      action: 'get_config'
    },
    headers
  });
}

function getUser() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let baseURL = arguments.length > 1 ? arguments[1] : undefined;
  return get({
    url: 'api/user',
    headers,
    baseURL: baseURL
  });
}

function getUserBillingInvoices() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return get({
    url: 'customer/invoices',
    headers,
    baseURL: "https://billing.simplywall.st/"
  });
}

function getUserViews() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return get({
    url: 'api/grid/view/myviews',
    headers
    // requiresAuthentication: true,
  });
}

function gridFilters(options) {
  let include = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'info,score,analysis';
  let headers = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const defaultOptions = {
    no_result_if_limit: false,
    offset: 0,
    size: 16,
    state: 'read'
  };
  return postJson({
    url: `api/grid/filter?include=${include}`,
    payload: Object.assign({}, defaultOptions, options),
    headers
  });
}

function updateGrid(gridId, payload) {
  let headers = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return patch({
    url: `api/grid/view/${gridId}`,
    headers,
    payload
  });
}

function updateUser(payload) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return patch({
    url: 'api/user',
    payload,
    headers
  });
}

function updateProfileImage(payload) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const formData = new FormData();
  formData.append('image', payload.image);
  return postFormData({
    url: 'api/user/image/profile',
    payload: formData,
    headers
  });
}

function getProfileImage() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return get({
    url: 'api/user/image/profile',
    headers
  });
}

function deleteProfileImage() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return del({
    url: 'api/user/image/profile',
    headers
  });
}

function userOtp() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return postJson({
    url: 'api/user/otp',
    headers
  });
}

function userRegister(payload) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return post({
    url: 'backend/auth/register',
    payload,
    headers
  });
}

function viewSearch(_ref) {
  let {
    offset = 0,
    size = 12
  } = _ref;
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const payload = {
    offset,
    rules: [['order_by', 'rank', 'desc'], ['order_by', 'create_date', 'asc']],
    size
  };
  return postJson({
    url: 'api/grid/view/search',
    headers,
    payload
  });
}

function getUserMailLists() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return get({
    url: 'api/user/mail/lists',
    headers
  });
}

function companyDevelopmentInfo(eventIdList) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return postJson({
    url: 'api/company/developments/info?include=types',
    payload: {
      ids: eventIdList
    },
    headers
  });
}

function getPortfolio(id, secret, refresh) {
  let headers = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  return get({
    url: `api/portfolio/analysis/${id}`,
    headers,
    payload: {
      secret,
      include: 'holdings.score.snowflake,items,score.snowflake,excluded_companies.score.snowflake,missing_companies,chart_data',
      refresh
    }
  });
}

const getDefaultPayload = function () {
  let isPublic = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
  let include = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ['items', 'items.transactions', 'items.company'];
  return {
    include: include.join(','),
    sharing: isPublic
  };
};
const getIdQuery = function () {
  let id = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return id !== '' ? `/${id}` : '';
};
/** Returns portfolio list */
function getPortfolios() {
  let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    id,
    isPublic,
    include
  } = options;
  const payload = getDefaultPayload(isPublic, include);
  return get({
    url: `api/user/portfolio${getIdQuery(id)}`,
    payload,
    headers
  });
}

function createPortfolio(payload) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return postJson({
    url: 'api/user/portfolio',
    headers,
    payload
  });
}

function updatePortfolio(options) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    id,
    payload
  } = options;
  return patch({
    url: `api/user/portfolio/${id}`,
    payload,
    headers
  });
}

function getPortfolioNews(id, secret) {
  let headers = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const payload = secret ? {
    secret
  } : '';
  return get({
    url: `api/user/portfolio/news/${id}`,
    headers,
    payload
  });
}

function getSharePortfolioHoldings(id, secret) {
  let headers = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return get({
    url: `api/user/portfolio/${id}`,
    headers,
    payload: {
      secret,
      include: 'items,items.transactions,items.company'
    }
  });
}

function getPriceHistory(options) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    companyId,
    payload
  } = options;
  return get({
    url: `api/company/price/${removeLeadingSlash(companyId || '')}`,
    headers,
    payload
  });
}

function createPortfolioTransaction(payload) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return post({
    url: 'api/user/portfolio/transaction',
    headers,
    payload
  });
}

function deletePortfolioTransaction(id) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return del({
    url: `api/user/portfolio/transaction/${id}`,
    headers
  });
}

function getCompany (_ref) {
  let {
    lookupVal,
    payload
  } = _ref;
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let axiosConfig = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const config = Object.assign({
    url: `api/company/${removeLeadingSlash(lookupVal)}`,
    payload,
    headers
  }, axiosConfig);
  return get(config);
}

function getCompanyStatements (_ref) {
  let {
    lookupVal,
    payload
  } = _ref;
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let axiosConfig = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  if (axiosConfig.baseURL === undefined) {
    axiosConfig.baseURL = "https://statements.simplywall.st/";
  }
  return get(Object.assign({
    url: `/statements/${encodeURIComponent(removeLeadingSlash(lookupVal))}`,
    payload,
    headers
  }, axiosConfig));
}

function getCompetitors (_ref) {
  let {
    lookupVal,
    payload
  } = _ref;
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let baseURL = arguments.length > 2 ? arguments[2] : undefined;
  return get({
    url: `api/competitors/${removeLeadingSlash(lookupVal)}`,
    payload,
    headers,
    baseURL: baseURL
  });
}

function getSearchCompany (query) {
  return get({
    url: `api/search/${query}`
  });
}

function addCompanyToPortfolio (payload) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return postJson({
    url: 'api/user/portfolio/item?include=company.score.snowflake,company.info',
    payload,
    headers
  });
}

function deleteCompanyFromPortfolio (companyPortfolioItemId) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return del({
    url: `api/user/portfolio/item/${companyPortfolioItemId}`,
    headers
    // requiresAuthentication: true,
    // resolveResponse: false,
  });
}

function getNews (lookupValue) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return get({
    url: `api/news/${removeLeadingSlash(lookupValue)}`,
    payload: {
      version: '2.0',
      'simplywall.st': true
    },
    headers
  });
}

function eventTypeReducer(acc, current) {
  acc[`event_type_ids[${current}]`] = 1;
  return acc;
}
/** mapping defined in /config/services/events.global.php */
/** new events added that was not in the old `eventTypeId` map
 * dividend: [45]
 * strategy: [23]
 * financial: [136, 87]
 * other: [192]
 * */
// prettier-ignore
const eventsMapping = {
  dividend: [152, 213, 214, 215, 230, 232, 233, 36, 45, 46, 47, 53, 54, 94].reduce(eventTypeReducer, {}),
  management: [101, 102, 16, 92].reduce(eventTypeReducer, {}),
  strategy: [1, 137, 21, 22, 23, 3, 31, 32, 41, 44, 63, 65, 73, 78, 80, 82].reduce(eventTypeReducer, {}),
  financial: [11, 135, 136, 150, 225, 226, 26, 27, 28, 29, 42, 43, 5, 59, 61, 74, 83, 85, 86, 87, 88, 93].reduce(eventTypeReducer, {}),
  other: [12, 153, 154, 155, 156, 157, 160, 163, 164, 172, 177, 187, 192, 194, 205, 206, 207, 224, 24, 25, 56, 57, 58, 62, 7, 75, 76, 77, 79, 89, 90, 91, 95, 97, 99].reduce(eventTypeReducer, {})
};
function companyDevelopments(_ref) {
  let {
    companyId,
    payload,
    categories
  } = _ref;
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const eventsPayload = categories.reduce((acc, current) => {
    if (current in eventsMapping) {
      return Object.assign({}, acc, eventsMapping[current]);
    }
    return acc;
  }, {});
  return get({
    url: `api/company/developments/${removeLeadingSlash(companyId)}`,
    payload: Object.assign({}, payload, eventsPayload),
    headers
  });
}

function getIndustryFields () {
  return get({
    url: `api/industry/fields`
  });
}

const getDefaultConfig = () => ({
  action: ''
});
const payloadDefaults = () => ({
  offset: 0,
  limit: 100
});
function getIndustryFieldFilter () {
  let config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : getDefaultConfig();
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    action = '',
    payload
  } = config;
  return get({
    url: `api/industry/fields/${action}`,
    payload: Object.assign({}, payloadDefaults, payload),
    headers
  });
}

const defaultConfig$1 = () => ({
  companyIdentifier: ''
});
function getIndustryCompany () {
  let config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultConfig$1();
  const {
    companyIdentifier,
    payload
  } = config;
  return get({
    url: `api/industry/company/${companyIdentifier}`,
    payload
  });
}

const defaultConfig = () => ({
  action: ''
});
function getIndustry () {
  let config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultConfig();
  const {
    action = '',
    payload
  } = config;
  return get({
    url: `api/industry/${action}`,
    payload
  });
}

function getUserCompanyLimit () {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return get({
    url: 'api/user/company/limit',
    headers
  });
}

function updateUserBilling(payload) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return patch({
    url: 'customer/billing',
    payload,
    headers,
    baseURL: "https://billing.simplywall.st/"
  });
}

function updateCreditCard(payload) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return postJson({
    url: 'customer/sources',
    payload,
    headers,
    baseURL: "https://billing.simplywall.st/"
  });
}

/*

    **** WARNING ****
    This endpoint is a bit whack and nothing more than a legacy hack. It adds
    exchanges to a user profile. These exchanges are (or the count of them)
    used to show that a user (if on a Learner Plan), has been through the
    "selectPlanModal" flow (AKA plan confirmation after the end of a trail).

*/
function updateUserExchanges(payload) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return postJson({
    url: 'api/user/exchange',
    payload,
    headers
  });
}

function cancelSubscription() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return patch({
    url: 'api/user/billing',
    payload: {
      gateway: 'stripe',
      plan: 'free'
    },
    headers
  });
}

function deleteCreditCard() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return del({
    url: 'customer/sources/delete',
    baseURL: "https://billing.simplywall.st/",
    headers
  });
}

function magicLink(options) {
  return postJson({
    url: `backend/auth/${options.type}/${options.email}`
  });
}

function passwordReset(options) {
  return postJson({
    url: 'api/user/password/reset',
    payload: options
  });
}

function getAccessTokenFromOtp(payload) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return get({
    url: `/backend/auth/otp`,
    payload,
    headers
  });
}

function preDeleteAccount () {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return postJson({
    url: 'api/user/delete',
    headers
  });
}

function deleteAccount(token) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return del({
    url: 'api/user/delete',
    payload: {
      token
    },
    headers
  });
}

function deletePortfolio(id) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return del({
    url: `api/user/portfolio/${id}`,
    headers
    // requiresAuthentication: true,
  });
}

function updatePortfolioItem(itemId, reinvestDividend) {
  let headers = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return patch({
    url: `api/user/portfolio/item/${itemId}`,
    payload: {
      reinvest: reinvestDividend
    },
    headers
  });
}

function getAnalystCoverage (req) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    lookupVal,
    payload
  } = req;
  return get({
    url: `/api/company/estimates/coverage/${lookupVal}`,
    payload,
    headers
  });
}

function getSecretCode () {
  return get({
    url: `api/tools/secret-code`
  });
}

function refreshJson(req) {
  const payload = Object.assign({}, req ? req : {});
  return post({
    url: 'backend/auth/token',
    payload
  });
}

function getIndustryAverage (options) {
  return get({
    url: `api/industry/${options}`
  });
}

function getCompanyIndustryAverage (lookup) {
  return get({
    url: `api/industry/company/${lookup}`
  });
}

function getPlans() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return get({
    url: `v2/plans`,
    headers,
    baseURL: "https://billing.simplywall.st/"
  });
}

function getCoupon(id) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return get({
    url: `api/user/billing/coupon`,
    payload: {
      ref: id
    },
    headers
  });
}

function deleteCoupon() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return del({
    url: 'customer/discount/delete',
    baseURL: "https://billing.simplywall.st/",
    headers
  });
}

function getCompanyTopShareholders (_ref) {
  let {
    lookupVal,
    payload
  } = _ref;
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let axiosConfig = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return get(Object.assign({
    url: `api/company/ownership/shareholders/${decodeURIComponent(lookupVal)}`,
    payload,
    headers
  }, axiosConfig));
}

function updateCoupon(id) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return patch({
    url: `api/user/billing`,
    payload: {
      coupon: id,
      gateway: 'stripe'
    },
    headers
  });
}

function getCompanyCsvFile (_ref) {
  let {
    lookupVal,
    payload
  } = _ref;
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let axiosConfig = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return get(Object.assign({
    url: `api/company/download/csv/${lookupVal}`,
    payload,
    headers
  }, axiosConfig));
}

function getIndustries (options) {
  const url = 'api/industry/tree';
  return get({
    url,
    payload: typeof options !== 'undefined' && Object.keys(options).length > 0 ? {
      filter_child_level: options === null || options === void 0 ? void 0 : options.filterChildLevel,
      country: options === null || options === void 0 ? void 0 : options.country
    } : undefined
  });
}

function getDashboardFeed(payload) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return get({
    url: `dashboard/events${payload.refresh ? '?refresh=1' : ''}`,
    headers
  });
}

function getDashboardRecentlyViewed() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return get({
    url: `dashboard/recently-viewed`,
    headers
  });
}

function getDashboardSuggestions() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return get({
    url: 'dashboard/suggestions',
    headers
  });
}

function getDashboardCompanyPerformance() {
  let market = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'us';
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return get({
    url: `dashboard/market-performance/${market}`,
    headers
  });
}

function getDashboardIndustryPerformance() {
  let market = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'us';
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return get({
    url: `dashboard/industry-performance/${market}`,
    headers
  });
}

function getDashboardMyMarketsAndIndustries() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return get({
    url: 'dashboard/my-markets-and-industries',
    headers
  });
}

function getStreamToken() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return get({
    url: 'dashboard/user/token',
    headers
  });
}

function getStreamTokenDirect() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return get({
    url: '/token',
    headers,
    baseURL: "https://stream.simplywall.st/"
  });
}

var getDashboardInternationalMarkets = (function () {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return get({
    url: 'dashboard/international-markets',
    headers
  });
});

var getDashboardUpcomingEvents = (function () {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return get({
    url: 'dashboard/upcoming-events',
    headers
  });
});

function getStripePortalUrl(req) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    returnUrl,
    planId
  } = req;
  const url = planId ? `portal/${planId}?return_url=${returnUrl}` : `portal?return_url=${returnUrl}`;
  return post({
    url,
    headers,
    baseURL: "https://billing.simplywall.st/"
  });
}

function getStripeCheckoutUrl(req) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    cancelPath,
    planId,
    userReturnPath,
    userReturnPathError,
    couponId
  } = req;
  const url = `checkout/session?price_id=${planId}&cancel_path=${cancelPath}${couponId && `&coupon=${couponId}`}${userReturnPath && `&user_return_path=${userReturnPath}`}${userReturnPathError && `&user_return_path_error=${userReturnPathError}`}`;
  return post({
    url,
    headers,
    baseURL: "https://billing.simplywall.st/"
  });
}

function updateUserCompanyLimits (lookup) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return postJson({
    url: `api/user/company/limit/${lookup.toLowerCase()}`,
    headers
  });
}

function checkCompany (_ref) {
  let {
    lookupVal,
    payload
  } = _ref;
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let axiosConfig = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const config = Object.assign({
    url: `api/company/${removeLeadingSlash(lookupVal)}`,
    payload,
    headers
  }, axiosConfig);
  return head(config);
}

var EventType$1;
(function (EventType) {
  EventType["Event"] = "event";
  EventType["Article"] = "article";
  EventType["Brief"] = "brief";
  EventType["ExternalArticle"] = "external-article";
})(EventType$1 || (EventType$1 = {}));
function getCompanyFeed(options) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const limitQuery = typeof options.limit === 'number' ? `?maxResults=${options.limit}` : '';
  return get({
    url: `dashboard/company/${removeLeadingSlash(options.lookup)}${limitQuery}`,
    headers
  });
}

/**
 * POST request used to retrieve information from grid "rules",
 * this way we grab the json rules and apply to the backend query.
 */
function getMarketIndustryForIdeas(options) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return postJson({
    url: 'api/industry/market-investing-ideas',
    payload: Object.assign({}, options),
    headers
  });
}

function getPortfolioCsvFile (_ref) {
  let {
    lookupVal,
    payload
  } = _ref;
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let axiosConfig = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return get(Object.assign({
    url: `api/user/portfolio/download/csv/${lookupVal}`,
    payload,
    headers
  }, axiosConfig));
}

function getMarketIndustry (market) {
  return get({
    url: `api/industry/market${market && market !== 'global' ? `/${market}` : ''}`
  });
}

function getDashboardEvent(payload) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return get({
    url: `dashboard/events/${payload.companyId.toLowerCase()}/${payload.activityId}`,
    headers
  });
}

function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }
  if (!value || !value.then) {
    value = Promise.resolve(value);
  }
  return then ? value.then(then) : value;
}
function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }
    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
function getGraphQLClient(config) {
  var _config$baseURL;
  const client = new GraphQLClient((_config$baseURL = config.baseURL) !== null && _config$baseURL !== void 0 ? _config$baseURL : "https://simplywall.st/graphql", {
    headers: Object.assign({
      'apollographql-client-version': "mono-auto-442c9cf4" 
    }, config.headers),
    fetch: _async(function (input, init) {
      return _await(fetch(input, init), function (response) {
        if (!response.ok) {
          if (response.headers.get('cf-mitigated') === 'challenge') {
            handleChallenge();
          }
        }
        return response;
      });
    })
  });
  return client;
}

function getProAPIGraphQLClient(config) {
  const client = new GraphQLClient("https://simplywall.st/backend/pro-api/graphql", {
    headers: Object.assign({
      'apollographql-client-version': "mono-auto-442c9cf4" 
    }, config.headers)
  });
  return client;
}

function getMarketIndustryPerformance() {
  let market = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'us';
  let industryId = arguments.length > 1 ? arguments[1] : undefined;
  let headers = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return get({
    url: `/api/market/${market}/industry-performance${industryId ? `/${industryId}` : ''}`,
    headers
  });
}

function getMarketMovers() {
  let market = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'us';
  let industryId = arguments.length > 1 ? arguments[1] : undefined;
  let headers = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return get({
    url: `/api/market/market-movers/${market}${industryId ? `/${industryId}` : ''}`,
    headers
  });
}

var EventType;
(function (EventType) {
  EventType["Event"] = "event";
  EventType["Article"] = "article";
  EventType["Brief"] = "brief";
  EventType["ExternalArticle"] = "external-article";
})(EventType || (EventType = {}));
function getMarketFeed(market, industryId) {
  let headers = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  let url = `api/market/activities/${market}`;
  if (industryId) {
    url += `/${industryId}`;
  }
  return get({
    url,
    headers
  });
}

function getMarketSummary(template, market, industry) {
  return get({
    url: 'getMarketSummary',
    baseURL: "https://swr.simplywall.st/",
    payload: {
      template,
      market,
      industry
    }
  });
}

function getMarketSummaries(market, industry) {
  return get({
    url: 'getMarketSummaries',
    baseURL: "https://swr.simplywall.st/",
    payload: {
      market,
      industry
    }
  });
}

function updateUserMailSubscription(_ref) {
  let {
    userId,
    subscriptionGroupId,
    status,
    headers = {}
  } = _ref;
  return postJson({
    url: 'api/user/mail/subscriptions',
    payload: {
      userId,
      subscriptionGroupId,
      status
    },
    headers
  });
}

/**
 * Validate industry url in the correct format or not
 * and return the industry detail for the correct one.
 * Otherwise, throw 404 error.
 * @param slug - url format: sector/primary/secondary/tertiary
 */
function validateIndustryUrl(slug) {
  return get({
    url: 'industry/details/',
    baseURL: "https://workers.simplywall.st/",
    payload: {
      slug
    }
  });
}

function getUserCompanyPeers (_ref) {
  let {
    companyId
  } = _ref;
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const config = {
    url: `backend/company-peers/user/peers/${companyId.toLowerCase()}`,
    headers
  };
  return get(config);
}

function updateUserCompanyPeers (_ref) {
  let {
    peerCollectionId,
    peerCollection
  } = _ref;
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const config = {
    url: `backend/company-peers/user/peers/${peerCollectionId}`,
    headers,
    payload: peerCollection
  };
  return put(config);
}

function createUserCompanyPeers (peerCollection) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const config = {
    url: `backend/company-peers/user/peers`,
    headers,
    payload: peerCollection
  };
  return postJson(config);
}

function deleteUserCompanyPeers (_ref) {
  let {
    peerCollectionId
  } = _ref;
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const config = {
    url: `backend/company-peers/user/peers/${peerCollectionId}`,
    headers,
    payload: {}
  };
  return del(config);
}

function resendEmailValidation(payload) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return post({
    url: '/backend/auth/validate/email/resend',
    payload,
    headers
  });
}

function deleteRefreshTokenAll() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return del({
    url: 'backend/auth/refresh_token/all',
    headers
  });
}

function getRewardfulAffiliateReferrals(payload) {
  return postJson({
    url: `api/affiliate/referrals`,
    payload
  });
}

function getAuthTokenInfo() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return get({
    url: `/backend/auth/token/info`,
    headers
  });
}

function getProApiKeys() {
  let headers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return get({
    url: 'backend/pro-api/keys',
    headers
  });
}

function createProApiToken(payload) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return post({
    url: 'backend/pro-api/keys',
    headers,
    payload
  });
}

function deleteProApiKey(id) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return del({
    url: `backend/pro-api/keys/${id}`,
    headers
  });
}

function createSupplementaryPlan(payload) {
  let headers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return post({
    url: 'api/user/billing/supplementary_plans',
    headers,
    payload
  });
}

export { addCompanyToPortfolio, authToken, cancelSubscription, checkCompany, checkGridView, companyDevelopments, companyDevelopmentInfo as companyDevelopmentsInfo, createGrid, createPortfolio, createPortfolioTransaction, createProApiToken, createSupplementaryPlan, createUserCompanyPeers, deleteAccount, deleteCompanyFromPortfolio, deleteCoupon, deleteCreditCard, deleteGridView, deletePortfolio, deletePortfolioTransaction, deleteProApiKey, deleteProfileImage, deleteRefreshTokenAll, deleteUserCompanyPeers, getAccessTokenFromOtp, getAnalystCoverage, getAuthTokenInfo, getCompany, getCompanyCsvFile, getCompanyFeed, getCompanyIndustryAverage, getCompanyStatements, getCompanyTopShareholders, getCompetitors, getCoupon, getDashboardCompanyPerformance, getDashboardEvent, getDashboardFeed, getDashboardIndustryPerformance, getDashboardInternationalMarkets, getDashboardMyMarketsAndIndustries, getDashboardRecentlyViewed, getDashboardSuggestions, getDashboardUpcomingEvents, getGraphQLClient, getGridView, getIndustries, getIndustry, getIndustryAverage, getIndustryCompany, getIndustryFieldFilter, getIndustryFields, getMarketFeed, getMarketIndustry, getMarketIndustryForIdeas, getMarketIndustryPerformance, getMarketMovers, getMarketSummaries, getMarketSummary, getNews, getPlans, getPortfolio, getPortfolioCsvFile, getPortfolioNews, getPortfolios, getPriceHistory, getProAPIGraphQLClient, getProApiKeys, getProfileImage, getRewardfulAffiliateReferrals, getSearchCompany, getSecretCode, getServices, getSharePortfolioHoldings, getStreamToken, getStreamTokenDirect, getStripeCheckoutUrl, getStripePortalUrl, getUacCheck, getUser, getUserBillingInvoices, getUserCompanyLimit, getUserCompanyPeers, getUserMailLists, getUserViews, gridFilters, magicLink, passwordReset, preDeleteAccount, queryStringFromObject, refreshJson as refreshToken, removeTrailingSlash, resendEmailValidation, setBaseAdapter, setBaseUrl, updateCoupon, updateCreditCard, updateGrid, updatePortfolio, updatePortfolioItem, updateProfileImage, updateUser, updateUserBilling, updateUserCompanyLimits, updateUserCompanyPeers, updateUserExchanges as updateUserExchange, updateUserMailSubscription, userOtp, userRegister, validateIndustryUrl, viewSearch };
